/**
 * Hilfsfunktionen fuer die formatierten Eingabefelder (RichListInput).
 *
 * Die Eingabekarten haben frueher reinen Text gespeichert, jetzt HTML aus
 * TipTap. Beide Formen muessen nebeneinander funktionieren:
 *
 *   - Bestandsdaten (reiner Text, oft mehrzeilig) duerfen beim Oeffnen im
 *     Editor KEINE Zeilenumbrueche verlieren — TipTap wuerde einen rohen
 *     Text-String als HTML interpretieren und alles zu einer Zeile machen.
 *   - Umgekehrt darf HTML nicht in Felder gelangen, die als Klartext
 *     weiterverarbeitet werden (z. B. die gepaarten Bloecke fuer Abschnitt 5).
 */

/** Enthaelt der Wert HTML-Markup? */
export function isHtml(value: string): boolean {
  if (!value) return false;
  return /<\/?[a-z][a-z0-9]*(\s[^>]*)?>/i.test(value);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Reiner Text → HTML-Absaetze, eine Zeile je Absatz. */
export function plainTextToHtml(text: string): string {
  if (!text) return '<p></p>';
  return text
    .split(/\r?\n/)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join('');
}

/**
 * HTML → Klartext, eine Zeile je Block. Reiner Text wird unveraendert
 * durchgereicht.
 */
export function htmlToPlainText(html: string): string {
  if (!html) return html;
  if (!isHtml(html)) return html;

  const container = document.createElement('div');
  container.innerHTML = html;
  container.querySelectorAll('script, style, noscript, template').forEach((el) => el.remove());

  const lines: string[] = [];

  const walk = (node: Element) => {
    for (const child of Array.from(node.children)) {
      const tag = child.tagName.toLowerCase();
      if (tag === 'ul' || tag === 'ol') {
        walk(child);
      } else if (tag === 'li') {
        // Verschachtelte Listen zaehlen als eigene Zeilen, nicht als Teil
        // des uebergeordneten Punktes.
        const clone = child.cloneNode(true) as Element;
        clone.querySelectorAll('ul, ol').forEach((n) => n.remove());
        const own = (clone.textContent || '').replace(/\s+/g, ' ').trim();
        if (own) lines.push(own);
        child.querySelectorAll(':scope > ul, :scope > ol').forEach((sub) => walk(sub));
      } else {
        const text = (child.textContent || '').replace(/\s+/g, ' ').trim();
        if (text) lines.push(text);
      }
    }
  };

  walk(container);

  if (lines.length === 0) {
    return (container.textContent || '').replace(/\s+/g, ' ').trim();
  }
  return lines.join('\n');
}

/**
 * Wert so aufbereiten, dass TipTap ihn verlustfrei laden kann.
 * Bestandsdaten in reinem Text werden dabei zu Absaetzen.
 */
export function toEditorContent(value: string): string {
  if (!value || !value.trim()) return '<p></p>';
  return isHtml(value) ? value : plainTextToHtml(value);
}

/** Ist der Editor-Inhalt inhaltlich leer? */
export function isEmptyContent(value: string): boolean {
  if (!value) return true;
  return htmlToPlainText(value).trim().length === 0;
}
